import PlayableEvent from '../Models/PlayableEvent'
import AnearEvent from '../Models/AnearEvent'
import AnearParticipant from '../Models/AnearParticipant'
import AnearGuestAvatar from '../Models/AnearGuestAvatar'
import PlayableZone from '../Models/PlayableZone'
import PlayableApp from '../Models/PlayableApp'
import PlayableVenue from '../Models/PlayableVenue'
import AnearVenue from '../Models/AnearVenue'
import ApiService from './ApiService'
import * as logger from 'loglevel'
import { prepareEventImageForUpload } from '../Utils/prepareEventImageForUpload'

export const AVATAR_MAX_EDGE_PX = 512

class AnearApi extends ApiService {

  login = async (user, password) => {
    const json = await this.post("sessions", {user, password}, false)
    return this.createAnearUser(json)
  }

  register = async (name, email, password) => {
    const json = await this.post("registrations", {name, email, password}, false)
    return this.createAnearUser(json)
  }

  requestPasswordReset = async (email) => {
    await this.post("password_resets", { email }, false)
  }

  requestEmailVerification = async (email) => {
    await this.post("email_verifications", { email }, false)
  }

  resetPassword = async (token, password) => {
    const json = await this.put("password_resets", token, { password }, false)
    return this.createAnearUser(json)
  }

  verifyEmail = async (token) => {
    const json = await this.put("email_verifications", token, {}, false)
    return this.createAnearUser(json)
  }

  getEventHostInvitation = async (token) => {
    const json = await this.get("event_host_invitations", { id: token }, false)
    return json.data.attributes
  }

  acceptEventHostInvitation = async (token) => {
    const json = await this.put("event_host_invitations", token, {}, false)
    return json.data.attributes
  }

  googleOauthUrl = () => {
    return `${this.api_base_url}/auth/google_oauth2`
  }

  getAccount = async () => {
    const json = await this.get("accounts", {}, false)
    return this.createAnearUser(json)
  }

  updateGuestAccount = async (id, name, guestAvatarKey) => {
    return this.updateAccount(id, {
      name,
      'guest-avatar-key': guestAvatarKey
    })
  }

  updateAccount = async (id, attributes) => {
    const json = await this.put(
      "accounts", id,
      attributes,
      false
    )
    return this.createAnearUser(json)
  }

  deleteAccount = async (id) => {
    await this.httpDelete("accounts", id)
  }

  abandonGuestSignup = async ({ keepalive = false } = {}) => {
    await this.postAction("accounts/abandon", false, keepalive)
  }

  relinquishExpiredGuest = async () => {
    await this.postAction("accounts/relinquish", false)
  }

  dataURItoBlob = (dataURI) => {
    let byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    let ab = new ArrayBuffer(byteString.length);
    let ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    let blob = new Blob([ab], {type: mimeString});
    return blob;
  }

  updateAvatarImage = async (user, dataURI) => {
    const blob = this.dataURItoBlob(dataURI)
    const profileId = user.profile.id
    const source = new File(
      [blob],
      `${user.name()}_avatar.jpg`,
      { type: blob.type || 'image/jpeg' }
    )
    const { file } = await prepareEventImageForUpload(source, { maxEdgePx: AVATAR_MAX_EDGE_PX })
    const imageBlob = await this.activeStorageUpload(user.name(), file)

    const json = await this.put("profiles", profileId, { avatar: imageBlob.signed_id}, false)
    return this.createAnearUser(json)
  }

  updateProfile = async (id, firstName, lastName, phone, homepage, bio) => {
    const json = await this.put(
      "profiles",
      id,
      {
        'first-name': firstName,
        'last-name': lastName,
        phone,
        homepage,
        bio,
      },
      false
    )
    return this.createAnearUser(json)
  }

  globalPlayableApps = async (filter={}) => {
    const json = await this.get("global_apps", {filter: filter}, false)
    return json.data.map(
      app => new PlayableApp(app, json.included)
    )
  }

  playableEvents = async (filter, needsLocation = true) => {
    const json = await this.get("playable_events", {filter: filter}, needsLocation)
    return json.data.map(
      anearEvent => new PlayableEvent(anearEvent, json.included)
    )
  }

  playableVenues = async () => {
    const json = await this.get("playable_venues", {}, false)
    return json.data.map(
      playableVenue => new PlayableVenue(playableVenue, json.included)
    )
  }

  localZones = async (filter) => {
    const json = await this.get("local_zones", {filter: filter}, true)
    return json.data.map(
      zone => new PlayableZone(zone, json.included)
    )
  }

  createEvent = async (zoneId, {name, description, participationRadius} = {}, requiresGeoLocation = false) => {
    const relationships = {zone: zoneId}
    // Use requiresGeoLocation parameter instead of just checking participationRadius
    // The app/zone may require geolocation even if participationRadius isn't set in the form
    const needsLocation = requiresGeoLocation || (participationRadius && participationRadius > 0)
    let flags = []
    const json = await this.post("events", {name, description, flags}, needsLocation, relationships)
    return new AnearEvent(json.data, json.included)
  }

  cloneEvent = async (anearEvent) => {
    const eventId = anearEvent.id
    const needsLocation = anearEvent.requiresGeoLocationToParticipate()
    const resource = `/events/${eventId}/clone`
    const json = await this.post(resource, {}, needsLocation, {})
    return new AnearEvent(json.data, json.included)
  }

  becomeEventParticipant = async (anearEvent) => {
    const relationships = {event: anearEvent.id}
    try {
      const json = await this.post(
        "event_participants",
        {},
        anearEvent.requiresGeoLocationToParticipate(),
        relationships
      )
      return new AnearParticipant(json.data, json.included)
    } catch (err) {
      // Idempotency: If the user already has a participant record for this event,
      // treat "become participant" as a rejoin and return the existing participant.
      //
      // This can happen when a user previously joined, then later lands on the event
      // in spectator mode (e.g., via URL navigation or after auth/token refresh).
      const msg = err?.message || ''
      if (msg.includes('already participating')) {
        const existing = await this.getEventParticipant(anearEvent.id)
        return existing
      }
      throw err
    }
  }

  getEventParticipant = async (eventId) => {
    const json = await this.get("event_participants", {id: eventId}, false)
    return new AnearParticipant(json.data, json.included)
  }

  getEvent = async (eventIdOrSlug) => {
    const json = await this.get("events", {id: eventIdOrSlug}, false)
    return new AnearEvent(json.data, json.included)
  }

  getEventClock = async (eventId) => {
    const json = await this.get(`events/${eventId}/clock`, {}, false)
    const attrs = json?.data?.attributes || {}
    return {
      serverNow: attrs['server-now'] || attrs.serverNow || attrs.server_now || null
    }
  }

  transitionEvent = async (eventId, state) => {
    const baseApiUrl = this.api_base_url || '/api/v1'
    const developerApiBaseUrl = baseApiUrl.replace(/\/v1\/?$/, '/developer/v1')
    const payload = {
      data: {
        type: 'transitions',
        attributes: { state },
        relationships: {
          event: {
            data: {
              type: 'events',
              id: eventId
            }
          }
        }
      }
    }

    const request = new Request(
      `${developerApiBaseUrl}/transitions`,
      {
        method: 'POST',
        headers: this.default_headers,
        body: JSON.stringify(payload)
      }
    )
    return this.issueRequest(request, false)
  }

  transitionPlayableEvent = async (eventId, state) => {
    const payload = this.formatPayload('transitions', { state }, {})
    const request = new Request(
      `${this.api_base_url}/events/${eventId}/transition`,
      {
        method: 'POST',
        headers: this.default_headers,
        body: JSON.stringify(payload)
      }
    )
    return this.issueRequest(request, false)
  }

  getVenue = async (venueIdOrSlug) => {
    const json = await this.get("venues", {id: venueIdOrSlug}, false)
    return new AnearVenue(json.data, json.included)
  }

  requestEventMediaUpload = async (
    eventId,
    { filename, contentType, byteSize, purpose = 'content_image' }
  ) => {
    const json = await this.post(
      "event_media_uploads",
      {
        event_id: eventId,
        filename,
        content_type: contentType,
        byte_size: byteSize,
        purpose
      },
      false
    )

    const attrs = json?.data?.attributes || {}
    return {
      uploadUrl: attrs["upload-url"],
      uploadFields: attrs["upload-fields"] || {},
      finalUrl: attrs["final-url"],
      key: attrs["key"],
      expiresAt: attrs["expires-at"],
      maxBytes: attrs["max-bytes"]
    }
  }

  //
  // Workflow Execution (generic, app-agnostic ANAPI-orchestrated workflows).
  // Triggered declaratively by the <anear-workflow> web component. The named
  // workflow + its input are validated/authorized server-side by
  // Workflows::Dispatch; this is just the participant-surface transport.
  //
  startWorkflow = async (workflow, input = {}) => {
    logger.debug(`API: POST workflows (${workflow})`)
    const json = await this.post("workflows", { workflow, input }, false)
    return json?.data?.attributes || {}
  }

  getWorkflowRun = async (runId, workflow) => {
    logger.debug(`API: GET workflows/${runId} (${workflow})`)
    const json = await this.get("workflows", { id: runId, workflow }, false)
    return json?.data?.attributes || {}
  }

  getGuestAvatarIndex = async () => {
    const json = await this.get("guest_avatars", {}, false)
    return new AnearGuestAvatar(json.data, json.included)
  }

  getAppBySlug = async (appSlug) => {
    const apps = await this.globalPlayableApps()
    const app = apps.find(playableApp =>
      playableApp.app.attributes.slug === appSlug
    )
    return app
  }

  createChildEvent = async (appSlug, parentEventId, parentEventName, requiresGeoLocation = false, initialContext = null) => {
    // Find app by slug to get app name and zone ID
    const playableApp = await this.getAppBySlug(appSlug)
    if (!playableApp) {
      throw new Error(`App with slug '${appSlug}' not found`)
    }

    // Get the zone ID from the child app, not the parent event
    const childZoneId = playableApp.zone.id

    const relationships = {
      zone: childZoneId,
      parent_event: parentEventId
    }

    // Naming convention:
    //   "<child app short-name> <parent event name>"
    // This keeps child events identifiable in "My Events" while still clearly tied to the parent event.
    const childAppAttrs = playableApp.app?.attributes || {}
    const childAppShortName =
      childAppAttrs['short-name'] ||
      childAppAttrs.shortName ||
      // Fallback: slug is better than nothing
      appSlug

    const trimmedParentEventName = typeof parentEventName === 'string' ? parentEventName.trim() : ''
    const childEventName = trimmedParentEventName
      ? `${childAppShortName} ${trimmedParentEventName}`
      : `${childAppShortName}`

    const attributes = {
      name: childEventName,
      description: `Child app embedded in parent event`,
      flags: []
    }

    // Include initial_context if provided (partial context object for AppM)
    // JSON:API uses kebab-case for attribute names (initial-context), not snake_case (initial_context)
    // If no initialContext provided, use the event name as the title
    if (initialContext && typeof initialContext === 'object' && !Array.isArray(initialContext)) {
      attributes['initial-context'] = initialContext
      logger.debug(`[AnearApi] Creating child event with initial-context:`, JSON.stringify(initialContext))
    } else {
      // In the absence of options, pass the event name as the title in initial-context
      attributes['initial-context'] = { title: childEventName }
      logger.debug(`[AnearApi] Creating child event with default initial-context (title from event name):`, JSON.stringify(attributes['initial-context']))
    }

    const json = await this.post(
      "events",
      attributes,
      requiresGeoLocation,
      relationships
    )
    return new AnearEvent(json.data, json.included)
  }

  getChildEvents = async (parentEventId) => {
    const resource = `/events/${parentEventId}/children`
    const json = await this.get(resource, {}, false)
    return json.data.map(event => new AnearEvent(event, json.included))
  }

  cancelEvent = async (eventId) => {
    // Note: Child event cancellation should be handled server-side when parent is canceled/closed.
    // The ABR does not have access to developer endpoints, so we rely on:
    // 1. Client-side lifecycle management via propagateExit calls
    // 2. Backend handling of child event cancellation (if implemented)
    // This method is a no-op for now.
    logger.debug(`cancelEvent called for event ${eventId} (handled via client-side propagateExit)`)
    return Promise.resolve()
  }
}

const anearApi = new AnearApi()

export default anearApi
